import * as XLSX from "xlsx";

import type { Account, AccountType } from "~/types/contabilidad";

export interface ParsedAccountLine {
  rowNumber: number;
  code: string;
  name: string;
  accountType: AccountType;
  isActive: boolean;
  error?: string;
  existsInDb?: boolean;
}

export interface AccountParseResult {
  lines: ParsedAccountLine[];
  errors: string[];
  summary: {
    toCreate: number;
    alreadyExists: number;
    invalid: number;
  };
}

// Valid account types
const VALID_ACCOUNT_TYPES: AccountType[] = [
  "activo",
  "pasivo",
  "patrimonio",
  "ingreso",
  "gasto",
];

// Column name mapping (supports Spanish and English)
const COLUMN_MAP: Record<string, keyof ParsedAccountLine> = {
  codigo: "code",
  código: "code",
  code: "code",
  nombre: "name",
  name: "name",
  tipo: "accountType",
  type: "accountType",
  activa: "isActive",
  activo: "isActive",
  active: "isActive",
};

// Account type normalization
const ACCOUNT_TYPE_MAP: Record<string, AccountType> = {
  activo: "activo",
  asset: "activo",
  pasivo: "pasivo",
  liability: "pasivo",
  patrimonio: "patrimonio",
  equity: "patrimonio",
  ingreso: "ingreso",
  income: "ingreso",
  revenue: "ingreso",
  gasto: "gasto",
  expense: "gasto",
};

/**
 * Parse boolean values from various formats
 */
function parseBoolean(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true; // Default to active
  }

  if (typeof value === "boolean") {
    return value;
  }

  const str = String(value).toLowerCase().trim();
  return (
    str === "si" ||
    str === "sí" ||
    str === "yes" ||
    str === "true" ||
    str === "1" ||
    str === "x"
  );
}

/**
 * Normalize account type string to valid AccountType
 */
function normalizeAccountType(value: unknown): AccountType | null {
  if (!value) return null;

  const str = String(value).toLowerCase().trim();
  return ACCOUNT_TYPE_MAP[str] || null;
}

/**
 * Parse Excel/CSV file and extract account data
 */
export async function parseAccountFile(
  file: File,
  existingAccounts: Account[]
): Promise<AccountParseResult> {
  const errors: string[] = [];
  const lines: ParsedAccountLine[] = [];

  // Create a set of existing account codes for quick lookup
  const existingCodes = new Set(
    existingAccounts.map((acc) => acc.code.toLowerCase())
  );

  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // Try to find "Cuentas" sheet, otherwise use first sheet
    let sheetName = workbook.SheetNames.find(
      (name) => name.toLowerCase() === "cuentas"
    );
    if (!sheetName) {
      sheetName = workbook.SheetNames[0];
    }

    if (!sheetName) {
      errors.push("El archivo no contiene hojas de cálculo");
      return {
        lines,
        errors,
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      };
    }

    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      errors.push("No se pudo leer la hoja de cálculo");
      return {
        lines,
        errors,
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      };
    }

    // Convert to JSON (array of arrays)
    const data = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      defval: "",
    });

    if (data.length < 2) {
      errors.push(
        "El archivo debe tener al menos una fila de encabezado y una fila de datos"
      );
      return {
        lines,
        errors,
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      };
    }

    // Parse header row to determine column indices
    const headerRow = data[0];
    if (!headerRow) {
      errors.push("No se encontró la fila de encabezado");
      return {
        lines,
        errors,
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      };
    }

    const columnIndices: Record<string, number> = {};
    headerRow.forEach((header, index) => {
      const headerStr = String(header || "")
        .toLowerCase()
        .trim();
      const mappedField = COLUMN_MAP[headerStr];
      if (mappedField) {
        columnIndices[mappedField] = index;
      }
    });

    // Verify required columns exist
    if (columnIndices.code === undefined) {
      errors.push("Columna 'Codigo' no encontrada en el archivo");
    }
    if (columnIndices.name === undefined) {
      errors.push("Columna 'Nombre' no encontrada en el archivo");
    }
    if (columnIndices.accountType === undefined) {
      errors.push("Columna 'Tipo' no encontrada en el archivo");
    }

    if (errors.length > 0) {
      return {
        lines,
        errors,
        summary: { toCreate: 0, alreadyExists: 0, invalid: 0 },
      };
    }

    // Track codes within this file to detect duplicates
    const codesInFile = new Map<string, number>();

    // Process data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      const code = String(row[columnIndices.code] || "").trim();

      // Skip completely empty rows
      if (!code) continue;

      const name = String(row[columnIndices.name] || "").trim();
      const rawAccountType = row[columnIndices.accountType];
      const accountType = normalizeAccountType(rawAccountType);
      const isActive =
        columnIndices.isActive !== undefined
          ? parseBoolean(row[columnIndices.isActive])
          : true;

      const parsedLine: ParsedAccountLine = {
        rowNumber: i + 1, // 1-indexed for user display
        code,
        name,
        accountType: accountType || ("activo" as AccountType), // Default to activo if invalid
        isActive,
      };

      // Validate the line
      const lineErrors: string[] = [];

      // Check for empty required fields
      if (!code) {
        lineErrors.push("Codigo es requerido");
      }
      if (!name) {
        lineErrors.push("Nombre es requerido");
      }
      if (!accountType) {
        lineErrors.push(
          `Tipo '${rawAccountType}' no válido. Use: ${VALID_ACCOUNT_TYPES.join(", ")}`
        );
      }

      // Check for duplicate code within the file
      const codeLower = code.toLowerCase();
      if (codesInFile.has(codeLower)) {
        lineErrors.push(`Codigo duplicado (ver fila ${codesInFile.get(codeLower)})`);
      } else {
        codesInFile.set(codeLower, i + 1);
      }

      // Check if code already exists in database
      if (existingCodes.has(codeLower)) {
        parsedLine.existsInDb = true;
        lineErrors.push("Codigo ya existe en la empresa");
      }

      if (lineErrors.length > 0) {
        parsedLine.error = lineErrors.join(". ");
      }

      lines.push(parsedLine);
    }

    if (lines.length === 0) {
      errors.push("No se encontraron líneas de datos válidas en el archivo");
    }
  } catch (error) {
    errors.push(
      `Error al leer el archivo: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }

  // Calculate summary
  const toCreate = lines.filter((l) => !l.error && !l.existsInDb).length;
  const alreadyExists = lines.filter((l) => l.existsInDb).length;
  const invalid = lines.filter((l) => l.error && !l.existsInDb).length;

  return {
    lines,
    errors,
    summary: {
      toCreate,
      alreadyExists,
      invalid,
    },
  };
}

/**
 * Get valid lines for import (no errors)
 */
export function getValidAccountLines(
  result: AccountParseResult
): ParsedAccountLine[] {
  return result.lines.filter((line) => !line.error);
}

/**
 * Check if there are any validation errors
 */
export function hasAccountValidationErrors(result: AccountParseResult): boolean {
  return result.errors.length > 0 || result.lines.some((line) => !!line.error);
}

/**
 * Check if there are any lines that can be imported
 */
export function hasImportableLines(result: AccountParseResult): boolean {
  return result.summary.toCreate > 0;
}
